import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';

interface VmBlock {
  /** i18n key suffix under pages.about.future. */
  key: string;
  icon: ReactNode;
  first?: boolean;
}

const BLOCKS: VmBlock[] = [
  {
    key: 'vision',
    first: true,
    icon: (
      <>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
  },
  {
    key: 'mission',
    icon: (
      <>
        <path d="M9 18h6M10 21h4" />
        <path d="M12 3a6 6 0 0 0-3.6 10.8c.5.4.8.9.9 1.5l.1.7h5.2l.1-.7c.1-.6.4-1.1.9-1.5A6 6 0 0 0 12 3z" />
      </>
    ),
  },
];

export const Section24 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.head}>
          <div className={styles.eyebrow}>
            {t('pages.about.future.eyebrow')}
          </div>
          <h2 className={styles.title}>
            {t.rich('pages.about.future.title', {
              highlight: (chunks) => (
                <span className={styles.highlight}>{chunks}</span>
              ),
            })}
          </h2>
        </div>

        <div className={styles.blocks}>
          {BLOCKS.map((block) => (
            <div
              key={block.key}
              className={block.first ? styles.block : styles.blockBordered}
            >
              <div className={styles.side}>
                <span className={styles.sideIcon}>
                  <svg viewBox="0 0 24 24" className={styles.glyph}>
                    {block.icon}
                  </svg>
                </span>
                <span className={styles.sideLabel}>
                  {t(`pages.about.future.${block.key}.label`)}
                </span>
              </div>
              <div>
                <p className={styles.big}>
                  {t.rich(`pages.about.future.${block.key}.big`, {
                    highlight: (chunks) => (
                      <span className="text-mf-blue marketing-dark:text-mf-coral">
                        {chunks}
                      </span>
                    ),
                  })}
                </p>
                <p className={styles.sub}>
                  {t.rich(`pages.about.future.${block.key}.sub`, {
                    bold: (chunks) => (
                      <b className="font-semibold text-mf-off-black-600 marketing-dark:text-white/90">
                        {chunks}
                      </b>
                    ),
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-[74px] pb-5',
  wrap: 'mx-auto max-w-[1200px] px-6 lg:px-10',
  head: 'mx-auto max-w-[720px] text-center',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  title:
    'font-inter text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[40px]',
  highlight: 'text-mf-blue marketing-dark:text-mf-coral',
  blocks: 'mt-12',
  block: 'grid grid-cols-1 gap-8 py-14 lg:grid-cols-[288px_1fr] lg:gap-14',
  blockBordered:
    'grid grid-cols-1 gap-8 border-t border-[#F0F0F0] marketing-dark:border-white/10 py-14 lg:grid-cols-[288px_1fr] lg:gap-14',
  side: 'lg:sticky lg:top-28 lg:self-start',
  sideIcon: [
    'mb-4 flex h-14 w-14 items-center justify-center rounded-2xl',
    'bg-[#EBF0F7] text-mf-blue',
    'marketing-dark:bg-mf-coral/15 marketing-dark:text-mf-coral',
  ].join(' '),
  glyph: 'h-7 w-7 fill-none stroke-current stroke-[1.7]',
  sideLabel:
    'text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  big: 'font-inter text-[28px] font-extrabold leading-[1.22] tracking-[-0.01em] text-mf-off-black-600 marketing-dark:text-white sm:text-[37px]',
  sub: 'mt-5 text-lg leading-[1.72] text-mf-gray-600 marketing-dark:text-white/62',
};
